import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { ApiService } from '../../core/api.service';
import { displayValue, statusBadgeClass, statusBadgeIcon } from '../../shared/status-display';

@Component({
  selector: 'app-attendance-report',
  standalone: true,
  imports: [
    FormsModule,
    NgFor,
    NgIf,
    NgClass,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule
  ],
  templateUrl: './attendance-report.component.html',
  styleUrl: './attendance-report.component.scss'
})
export class AttendanceReportComponent implements OnInit {
  rows: any[] = [];
  workers: any[] = [];
  machines: any[] = [];
  pageIndex = 0;
  pageSize = 10;
  pageSizeOptions = [10, 25, 50, 100];
  rangeStart: Date | null = new Date();
  rangeEnd: Date | null = new Date();
  filters = {
    startDate: this.toDateInput(new Date()),
    endDate: this.toDateInput(new Date()),
    preset: 'today',
    worker: '',
    machine: '',
    status: ''
  };

  constructor(private api: ApiService) {}

  /**
   * Loads report filter lookups and the initial attendance report.
   * @returns void.
   */
  ngOnInit() {
    this.api.get<any[]>('/workers').subscribe((rows) => (this.workers = rows));
    this.api.get<any[]>('/machines').subscribe((rows) => (this.machines = rows));
    this.load();
  }

  /**
   * Fetches attendance report rows for the current filter state.
   * @returns void.
   */
  load() {
    const params = new URLSearchParams();
    Object.entries(this.filters).forEach(([key, value]) => {
      if (value && key !== 'preset') params.set(key, value);
    });
    this.api.get<any[]>(`/reports/attendance?${params.toString()}`).subscribe((rows) => {
      this.rows = rows || [];
      this.clampPage();
    });
  }

  /**
   * Applies a predefined date range and reloads the report.
   * @param preset Date preset key.
   * @returns void.
   */
  applyPreset(preset: string) {
    this.filters.preset = preset;
    const now = new Date();
    const start = new Date(now);
    const end = new Date(now);
    if (preset === 'yesterday') {
      start.setDate(start.getDate() - 1);
      end.setDate(end.getDate() - 1);
    }
    if (preset === 'week') {
      const day = start.getDay() || 7;
      start.setDate(start.getDate() - day + 1);
    }
    if (preset === 'month') {
      start.setDate(1);
    }
    if (!preset) {
      this.rangeStart = null;
      this.rangeEnd = null;
      this.filters.startDate = '';
      this.filters.endDate = '';
    } else if (preset !== 'custom') {
      this.rangeStart = start;
      this.rangeEnd = end;
      this.filters.startDate = this.toDateInput(start);
      this.filters.endDate = this.toDateInput(end);
    }
    this.load();
  }

  get pagedRows() {
    const start = this.pageIndex * this.pageSize;
    return this.rows.slice(start, start + this.pageSize);
  }

  paginationLabel() {
    const total = this.rows.length;
    if (!total) return 'Showing 0 records';
    const start = this.pageIndex * this.pageSize + 1;
    const end = Math.min(start + this.pageSize - 1, total);
    return `Showing ${start} to ${end} of ${total} records`;
  }

  currentPage() {
    return this.pageIndex + 1;
  }

  totalPages() {
    return Math.max(Math.ceil(this.rows.length / this.pageSize), 1);
  }

  canPreviousPage() {
    return this.pageIndex > 0;
  }

  canNextPage() {
    return this.pageIndex < this.totalPages() - 1;
  }

  firstPage() {
    this.pageIndex = 0;
  }

  previousPage() {
    if (this.canPreviousPage()) this.pageIndex -= 1;
  }

  nextPage() {
    if (this.canNextPage()) this.pageIndex += 1;
  }

  lastPage() {
    this.pageIndex = this.totalPages() - 1;
  }

  onPageSizeChange(value: number) {
    this.pageSize = Number(value) || 10;
    this.pageIndex = 0;
    this.clampPage();
  }

  workerInitials(row: any) {
    const name = String(row?.worker?.name || '').trim();
    if (!name) return '--';
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length > 1) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }

  workerAvatarClass(row: any) {
    const seed = String(row?.worker?.employeeId || row?.worker?.name || '').charCodeAt(0) || 0;
    return `avatar-${seed % 6}`;
  }

  statusClass(value: any) {
    return statusBadgeClass(value);
  }

  statusIcon(value: any) {
    return statusBadgeIcon(value);
  }

  displayCell(value: any) {
    return displayValue(value);
  }

  displayDate(value: string) {
    return displayValue(this.formatDate(value));
  }

  overviewCards() {
    const total = this.rows.length;
    const percent = (value: number) => (total ? `${((value / total) * 100).toFixed(1)}%` : '0.0%');
    const count = (predicate: (row: any) => boolean) => this.rows.filter(predicate).length;
    const sum = (selector: (row: any) => number) => this.rows.reduce((totalValue, row) => totalValue + Number(selector(row) || 0), 0);
    const statusIncludes = (row: any, value: string) => String(row?.status || '').toLowerCase().includes(value);
    const completed = count((row) => statusIncludes(row, 'completed') || statusIncludes(row, 'approved'));
    const pending = count((row) => statusIncludes(row, 'pending') || statusIncludes(row, 'review') || statusIncludes(row, 'progress') || statusIncludes(row, 'checked'));
    const rejected = count((row) => statusIncludes(row, 'reject') || Number(row.rejected || 0) > 0);
    const produced = sum((row) => row.produced);
    const rejectedQty = sum((row) => row.rejected);

    return [
      { label: 'Total Records', value: total, icon: 'event_note', tone: 'blue', meta: this.rangeLabel() },
      { label: 'Completed', value: completed, icon: 'check_circle', tone: 'green', meta: percent(completed) },
      { label: 'Pending / Active', value: pending, icon: 'pending_actions', tone: 'amber', meta: percent(pending) },
      {
        label: 'Issues',
        value: rejected,
        icon: 'report',
        tone: 'red',
        meta: rejectedQty ? `${new Intl.NumberFormat('en-IN').format(rejectedQty)} rejected` : percent(rejected)
      },
      { label: 'Produced Qty', value: produced, icon: 'trending_up', tone: 'purple', meta: 'Total output' }
    ];
  }

  formatOverviewValue(value: number | string) {
    if (typeof value === 'number') return new Intl.NumberFormat('en-IN').format(value);
    return value;
  }

  trackByOverviewLabel(_: number, card: { label: string }) {
    return card.label;
  }

  private clampPage() {
    this.pageIndex = Math.min(this.pageIndex, this.totalPages() - 1);
  }

  /**
   * Applies a custom date range selected by the date picker.
   * @returns void.
   */
  onRangeChange() {
    this.filters.preset = 'custom';
    this.filters.startDate = this.rangeStart ? this.toDateInput(this.rangeStart) : '';
    this.filters.endDate = this.rangeEnd ? this.toDateInput(this.rangeEnd) : this.filters.startDate;
    this.load();
  }

  /**
   * Clears all filters and reloads the report without constraints.
   * @returns void.
   */
  clearFilters() {
    this.rangeStart = null;
    this.rangeEnd = null;
    this.filters = {
      startDate: '',
      endDate: '',
      preset: '',
      worker: '',
      machine: '',
      status: ''
    };
    this.load();
  }

  rangeLabel() {
    if (!this.filters.startDate && !this.filters.endDate) return 'All records';
    if (this.filters.startDate && this.filters.endDate && this.filters.startDate !== this.filters.endDate) {
      return `${this.filters.startDate} to ${this.filters.endDate}`;
    }
    return this.filters.startDate || this.filters.endDate || 'Filtered';
  }

  /**
   * Exports the currently loaded attendance report rows as CSV.
   * @returns void.
   */
  exportCsv() {
    const headers = ['Worker', 'Machine', 'Status', 'Target', 'Produced', 'Rejected', 'Rejection Reason', 'Reason', 'Updated At'];
    const lines = [headers.join(',')].concat(
      this.rows.map((row) =>
        [
          row.worker?.name,
          row.machine?.machineId,
          row.status,
          row.target,
          row.produced,
          row.rejected,
          row.rejectionReason,
          row.reason,
          this.formatDate(row.updatedAt)
        ].map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(',')
      )
    );
    this.download('attendance-report.csv', lines.join('\n'), 'text/csv');
  }

  /**
   * Exports the currently loaded attendance report rows as formatted JSON.
   * @returns void.
   */
  exportJson() {
    this.download('attendance-report.json', JSON.stringify(this.rows, null, 2), 'application/json');
  }

  /**
   * Downloads generated report content using a temporary object URL.
   * @param filename Download file name.
   * @param content File content.
   * @param type MIME type.
   * @returns void.
   */
  private download(filename: string, content: string, type: string) {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Converts a Date to an HTML date-input value.
   * @param value Date to format.
   * @returns yyyy-mm-dd string.
   */
  private toDateInput(value: Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Formats report timestamps for display and CSV export.
   * @param value Date string.
   * @returns Localized date/time label.
   */
  formatDate(value: string) {
    return value ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(value)) : '';
  }
}
